import React, { useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Container, 
  Typography, 
  Button, 
  Box, 
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import { useContent } from '../contexts/ContentContext';

const Header = () => {
  const { language, toggleLanguage } = useLanguage();
  const { theme, toggleTheme } = useCustomTheme();
  const { content } = useContent();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  const t = content[language] || content.ar;
  const isActive = (path) => location.pathname === path;

  const navItems = [
    { path: '/', label: t.nav.home },
    { path: '/services', label: t.nav.services },
    { path: '/why-alrasheed', label: language === 'ar' ? 'لماذا الرشيد؟' : 'Why Al-Rasheed?' },
    { path: '/contact', label: t.nav.contact },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center', width: 250 }}>
      <List>
        {navItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              component={Link}
              to={item.path}
              selected={isActive(item.path)}
              sx={{
                color: isActive(item.path) ? 'primary.main' : 'text.primary',
                fontWeight: isActive(item.path) ? 600 : 400,
              }}
            >
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Box sx={{ p: 2, display: 'flex', gap: 2, justifyContent: 'center' }}>
        <Button
          onClick={toggleTheme}
          startIcon={theme === 'light' ? <Brightness4Icon /> : <Brightness7Icon />}
          variant="outlined"
          size="small"
        >
          {theme === 'light' ? (language === 'ar' ? 'داكن' : 'Dark') : (language === 'ar' ? 'فاتح' : 'Light')}
        </Button>
        <Button
          onClick={toggleLanguage}
          variant="outlined"
          size="small"
        >
          {language === 'ar' ? 'English' : 'العربية'}
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      <AppBar position="sticky" elevation={2} sx={{ bgcolor: 'background.paper', color: 'text.primary' }}>
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ py: 1 }}>
            <Box
              component={Link}
              to="/"
              sx={{
                textDecoration: 'none',
                color: 'inherit',
                flexGrow: { xs: 1, md: 0 },
                mr: { md: 4 }
              }}
            >
              <Typography
                variant="h5"
                component="div"
                sx={{
                  fontWeight: 700,
                  color: 'primary.main',
                }}
              >
                {t.siteName}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: 'block',
                }}
              >
                {t.tagline}
              </Typography>
            </Box>

            {isMobile ? (
              <>
                <Box sx={{ flexGrow: 1 }} />
                <IconButton
                  color="inherit"
                  aria-label="open drawer"
                  edge="start"
                  onClick={handleDrawerToggle}
                >
                  <MenuIcon />
                </IconButton>
              </>
            ) : (
              <>
                <Box sx={{ flexGrow: 1, display: 'flex', gap: 1 }}>
                  {navItems.map((item) => (
                    <Button
                      key={item.path}
                      component={Link}
                      to={item.path}
                      sx={{
                        color: isActive(item.path) ? 'primary.main' : 'text.primary',
                        fontWeight: isActive(item.path) ? 600 : 400,
                        textTransform: 'none',
                        '&:hover': {
                          bgcolor: 'action.hover',
                        }
                      }}
                    >
                      {item.label}
                    </Button>
                  ))}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton
                    onClick={toggleTheme}
                    color="inherit"
                    aria-label="toggle theme"
                  >
                    {theme === 'light' ? <Brightness4Icon /> : <Brightness7Icon />}
                  </IconButton>
                  <Button
                    onClick={toggleLanguage}
                    variant="outlined"
                    size="small"
                    sx={{ textTransform: 'none' }}
                  >
                    {language === 'ar' ? 'English' : 'العربية'}
                  </Button>
                </Box>
              </>
            )}
          </Toolbar>
        </Container>
      </AppBar>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Header;
